import { useMemo } from 'react';
import { getNullTextOfFilter, unitConversion } from '@/pages/datalab/utils/utils';
import { LIGHT_COLOR_ARR } from '../_config/_constant';
import {
  ChartOption,
  IBaseOptionsProps
} from '../_config/_interface';
import { BROWSER_LANGUAGE } from '@gp/constants/const';
/**
 * 图表配置Hook
 * 用于基础的ECharts配置选项
 * @param props - 包含图表配置的属性对象
 * @returns 水平条形图的ECharts配置选项
 */
export const useBaseOptions = (props: IBaseOptionsProps): ChartOption => {
  // 从props中解构出需要的属性
  const { series, xAxisData, legendData, tooltipFormatter, setOtherConfigFunc } = props;
  // 使用useMemo缓存计算结果，避免不必要的重复计算
  const option = useMemo((): ChartOption => {
    // 基础图表配置
    const baseOption: ChartOption = {
      // 图表网格布局配置
      grid: {
        left: BROWSER_LANGUAGE?.startsWith('en-') ? 6 : 4,
        top: 20,
        right: 4,
        bottom: 25,
        containLabel: true
      },
      // 图表颜色方案
      color: LIGHT_COLOR_ARR,
      // 提示框配置
      tooltip: {
        className: 'dbl-new-chart-tooltip-wrap',
        // backgroundColor: 'rgba(38, 40, 50, 0.75)', // 提示框背景颜色
        appendToBody: true, // 提示框是否添加到body中
        trigger: 'axis', // 触发类型，坐标轴触发
        confine: true, // 是否将提示框限制在图表区域内
        enterable: true, // 鼠标是否可进入提示框浮层中
        showDelay: 5, // 显示延迟，添加显示延迟可以避免频繁切换，单位ms
        padding: 0, // 提示框内边距
        borderWidth: 0, // 提示框边框宽度
        // 坐标轴指示器配置
        axisPointer: {
          type: 'shadow', // 指示器类型，阴影指示器
          z: 10, // 图形z轴方向的高度
          // 坐标轴指示器标签配置
          label: {
            show: true,
            backgroundColor: '#ffffff',
            fontSize: 10,
            color: '#262832',
            fontWeight: 700,
          },
          // 坐标轴指示器线型配置
          lineStyle: {
            color: '#cccccc', // 线条颜色
            width: 1, // 线条宽度
            type: 'solid' // 线条类型
          },
        },
        formatter: tooltipFormatter, // 提示框内容格式化函数
      },
      barMaxWidth: 80, // 柱状图最大宽度
      barCategoryGap: "50%", // 柱状图类目间距
      // Y轴配置
      yAxis: [
        {
          type: 'value',
          splitLine: {
            // 分割线效果
            show: true,
            lineStyle: {
              type: 'dashed',
              color: '#ddd',
            },
          },
          axisLabel: {
            color: '#838697',
            fontSize: 12,
            formatter(value: any) {
              return value ? unitConversion(value, true) : value;
            },
          },
        },
      ],
      // X轴配置
      xAxis: {
        type: 'category',
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          color: '#838697',
          fontSize: 10,
          align: 'center',
        },
        axisLine: {
          lineStyle: {
            color: '#D8DAE4',
          },
          show: true,
        },
        data: xAxisData?.map(x => getNullTextOfFilter(x)) ?? [],
      },
      // 图例配置
      legend: {
        left: 'center', // 图例水平位置
        bottom: -6, // 图例垂直位置
        type: 'scroll', // 图例类型，可滚动
        icon: 'circle', // 图例标记图形类型
        inactiveBorderWidth: 0, // 图例关闭时icon圆点的描边粗细
        itemHeight: 8, // 图例标记的高度
        itemWidth: 8, // 图例标记的宽度
        itemGap: 20, // 图例每项之间的间隔
        pageIconColor: '#444444', // 翻页按钮颜色
        pageIconInactiveColor: '#888888', // 翻页按钮禁用时的颜色
        pageIconSize: 12, // 翻页按钮大小
        pageButtonPosition: 'end', // 翻页按钮位置
        textStyle: {
          // 图例文字样式
          color: '#838697', // 文字颜色
          fontSize: 12, // 文字大小
          lineHeight: 16, // 行高
          padding: [2, 0, 0, 0], // 文字内边距
        },
        data: legendData ?? [], // 图例数据
        // 图例文本格式化函数，删除文本中的换行符，避免图例位置不对
        formatter: (value: string) => value?.replace?.(/\n|@$/g, '') || value,
      },
      // 系列数据配置
      series: series ?? []
    };

    // 如果有额外的配置函数，则应用该函数
    return setOtherConfigFunc ? setOtherConfigFunc(baseOption) : baseOption;
  }, [tooltipFormatter, xAxisData, legendData, series, setOtherConfigFunc]);

  // 返回配置选项
  return option;
};